import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';

import TextInput from '../common/formControls/TextInput';
import Header from '../menus/Header';
import { selectUser } from '../../features/auth/authSlice';
import { useAppDispatch } from '../../app/hooks';
import { showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';

const ACCENT = '#2e453d';
const BORDER = '#e4e4e0';

const SPECIAL_CHARACTER_REGEX = /[!@#$%^&*(),.?":{}|<>_\-+=~`[\]\\;'/]/;

const ChangePassword = () => {
  const storeDispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [passwordText, setPasswordText] = useState<string>('');
  const [password, setPassword] = useState<IPassword>({ error: false, errorText: '' });
  const [confirmPasswordText, setConfirmPasswordText] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<IPassword>({ error: false, errorText: '' });

  const [changePassword, { loading }] = useMutation(UPDATEUSER, {
    variables: { _id: user._id, record: { password: passwordText } },
    onCompleted: () => {
      storeDispatch(showSuccessSnackbar('Password has been updated'));
      navigate(-1);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useEffect(() => {
    passwordValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordText, confirmPasswordText]);

  const passwordValidation = () => {
    if (!passwordText && !confirmPasswordText) {
      setPassword({ error: false, errorText: '' });
      setConfirmPassword({ error: false, errorText: '' });
      return;
    }

    if (passwordText.length < 8) {
      setPassword({ error: true, errorText: 'Password must be at least 8 characters long' });
    } else if (!SPECIAL_CHARACTER_REGEX.test(passwordText)) {
      setPassword({ error: true, errorText: 'Password must include at least one special character' });
    } else {
      setPassword({ error: false, errorText: '' });
    }

    if (confirmPasswordText && confirmPasswordText !== passwordText) {
      setConfirmPassword({ error: true, errorText: 'Passwords do not match' });
    } else {
      setConfirmPassword({ error: false, errorText: '' });
    }
  };

  const isValid =
    passwordText.length >= 8 &&
    SPECIAL_CHARACTER_REGEX.test(passwordText) &&
    confirmPasswordText.length > 0 &&
    confirmPasswordText === passwordText;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'password') {
      setPasswordText(e.target.value);
    } else if (e.target.name === 'confirmPassword') {
      setConfirmPasswordText(e.target.value);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    changePassword();
  };

  return (
    <div>
      <Header />
      <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '450px',
            p: { xs: 3, sm: 4 },
            border: `1px solid ${BORDER}`,
            borderRadius: '10px',
            backgroundColor: '#fff',
          }}
        >
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: ACCENT, mb: 1 }}>Change Password</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#6a6a67', mb: 3 }}>
            Your new password must be at least 8 characters long and include at least one special character.
          </Typography>
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <Box sx={{ mb: 2 }}>
              <TextInput
                error={password.error}
                type="password"
                required
                name="password"
                label="New Password"
                placeholder="New Password"
                helperText={password.errorText}
                value={passwordText}
                handleTextInput={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextInput
                error={confirmPassword.error}
                type="password"
                required
                name="confirmPassword"
                label="Confirm New Password"
                placeholder="Confirm New Password"
                helperText={confirmPassword.errorText}
                value={confirmPasswordText}
                handleTextInput={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
              />
            </Box>
            <Button type="submit" variant="contained" disabled={!isValid || loading} sx={{ backgroundColor: ACCENT, textTransform: 'none' }}>
              Submit
            </Button>
          </form>
        </Box>
      </Box>
    </div>
  );
};

interface IPassword {
  error: boolean;
  errorText: string;
}

const UPDATEUSER = gql`
  mutation userUpdateById($_id: MongoID!, $record: UpdateByIdUserInput!) {
    userUpdateById(_id: $_id, record: $record) {
      record {
        _id
      }
    }
  }
`;

export default ChangePassword;
